import { useQuery } from '@tanstack/react-query';
import type { CallEventType, CallSource, CallStatus } from '@brimo/shared';
import { api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/** Types and requests of the telephony routes (docs/dev/api-dialer.md „Telephony"). */

export interface PhonePool {
  label: string;
  mask: string;
}

export interface TelephonyStatus {
  configured: boolean;
  /** „Telefonie ist nicht eingerichtet." while not configured. */
  message: string | null;
  pool: PhonePool | null;
  recording: boolean;
  /** Live transcription (D-218): the call screens show „Live-Transkript". */
  transcription: boolean;
}

export interface VoiceToken {
  token: string;
  identity: string;
  ttlSeconds: number;
  edge: string;
  /** Inbound calls ring this browser (dialer users). */
  incoming: boolean;
}

export type CallDispositionValue =
  | 'appointment_set'
  | 'follow_up'
  | 'not_reached'
  | 'no_interest'
  | 'wrong_number'
  | 'block'
  | 'voicemail'
  | 'callback_requested'
  | 'documents_promised';

export interface CallView {
  id: string;
  contactId: string;
  direction: 'inbound' | 'outbound';
  status: CallStatus;
  to: string | null;
  toDisplay: string | null;
  from: string | null;
  poolLabel: string | null;
  attemptNumber: number | null;
  startedAt: string;
  connectedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  disposition: CallDispositionValue | null;
  recording: { status: 'none' | 'pending' | 'available'; href: string | null };
  /** AI summary with the suggested disposition/follow-up and the post-call coaching (docs/dev/api-dialer.md „AI"). */
  summary: string | null;
  suggestedDisposition: CallDispositionValue | null;
  suggestedFollowUpAt: string | null;
  coachingFeedback: string | null;
}

export interface StartCallInput {
  contactId: string;
  contactPhoneId?: string | undefined;
  source: CallSource;
  sessionId?: string | undefined;
  taskId?: string | undefined;
}

export const telephonyKeys = {
  status: () => [...queryRoots.telephony, 'status'] as const,
};

export function useTelephonyStatus(enabled = true) {
  return useQuery({
    queryKey: telephonyKeys.status(),
    queryFn: () => api<TelephonyStatus>('/telephony/status'),
    enabled,
    staleTime: 60_000,
  });
}

export function fetchVoiceToken(): Promise<VoiceToken> {
  return api<VoiceToken>('/telephony/token', { method: 'POST', body: {} });
}

/** The browser softphone is (`true`) or is no longer (`false`) registered for inbound calls. */
export function reportPresence(registered: boolean): Promise<{ heartbeatSeconds: number }> {
  return api<{ heartbeatSeconds: number }>('/telephony/presence', { method: 'POST', body: { registered } });
}

export function createCall(input: StartCallInput): Promise<CallView> {
  const body: Record<string, string> = { contactId: input.contactId, source: input.source };
  if (input.contactPhoneId) body['contactPhoneId'] = input.contactPhoneId;
  if (input.sessionId) body['sessionId'] = input.sessionId;
  if (input.taskId) body['taskId'] = input.taskId;
  return api<CallView>('/calls', { method: 'POST', body });
}

export function reportCallEvent(
  callId: string,
  body: { type: CallEventType; at: string; reason?: string | undefined },
): Promise<CallView> {
  const payload: Record<string, string> = { type: body.type, at: body.at };
  if (body.reason) payload['reason'] = body.reason.slice(0, 200);
  return api<CallView>(`/calls/${callId}/events`, { method: 'POST', body: payload });
}

export interface CallSummary {
  status: 'generated' | 'stored' | 'no_transcript' | 'unavailable' | 'failed';
  summary: string | null;
  suggestedDisposition: CallDispositionValue | null;
  suggestedFollowUpAt: string | null;
}

/** `POST /dialer/calls/:id/summary`: summary and suggested disposition from the transcript so far. */
export function requestCallSummary(callId: string): Promise<CallSummary> {
  return api<CallSummary>(`/dialer/calls/${callId}/summary`, { method: 'POST', body: {} });
}
